/** Breadcrumb positioning consumes the header's published geometry only. */
export class BreadcrumbsManager {
    private static instance: BreadcrumbsManager | undefined;
    private readonly sections: HTMLElement[];
    private readonly observer: MutationObserver;
    private readonly resizes: ResizeObserver;
    private frame: number | null = null;

    private constructor() {
        this.sections = [
            ...document.querySelectorAll<HTMLElement>(
                "[data-breadcrumbs-section]",
            ),
        ];
        this.observer = new MutationObserver(this.schedule);
        this.resizes = new ResizeObserver(this.schedule);
        if (!this.sections.length) return;

        this.observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["style"],
        });
        for (const section of this.sections) {
            this.resizes.observe(section);
            this.observer.observe(section, {
                attributes: true,
                attributeFilter: ["data-header-reduction"],
            });
        }
        window.addEventListener("scroll", this.schedule, { passive: true });
        window.addEventListener("resize", this.schedule, { passive: true });
        this.update();
    }

    public static use(reset = false): BreadcrumbsManager {
        if (reset) {
            BreadcrumbsManager.instance?.destroy();
            BreadcrumbsManager.instance = undefined;
        }
        return (BreadcrumbsManager.instance ??= new BreadcrumbsManager());
    }

    public destroy(): void {
        this.observer.disconnect();
        this.resizes.disconnect();
        window.removeEventListener("scroll", this.schedule);
        window.removeEventListener("resize", this.schedule);
        if (this.frame !== null) cancelAnimationFrame(this.frame);
        this.frame = null;
        for (const section of this.sections) {
            delete section.dataset.behaviour;
            delete section.dataset.placement;
            section.removeAttribute("data-sticky");
            section.style.removeProperty("--breadcrumbs-offset");
        }
        document.body.style.removeProperty("--sticky-content-height");
    }

    private schedule = (): void => {
        if (this.frame !== null) return;
        this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
        });
    };

    private update(): void {
        const geometry = getComputedStyle(document.body);
        const behaviour =
            geometry.getPropertyValue("--header-behaviour").trim() || "static";
        const headerHeight =
            parseFloat(geometry.getPropertyValue("--header-height")) || 0;
        const headerBottom =
            parseFloat(geometry.getPropertyValue("--header-bottom")) || 0;
        const headerOffset =
            parseFloat(geometry.getPropertyValue("--header-offset")) || 0;
        let occupiedHeight = 0;

        for (const section of this.sections) {
            const internal = section.closest("header") !== null;
            section.dataset.placement = internal ? "header" : "content";
            const persistent = !(section.dataset.headerReduction || "")
                .split(",")
                .includes("breadcrumbs");
            const mode = !internal && persistent ? behaviour : "static";
            section.dataset.behaviour = mode;
            const transform = getComputedStyle(section).transform;
            const matrix = transform.match(/^matrix\(([^)]+)\)$/);
            const translation =
                mode === "reveal" && matrix
                    ? Number(matrix[1].split(",")[5]) || 0
                    : 0;
            const sectionTop =
                section.getBoundingClientRect().top - translation;
            const sticky =
                (mode === "sticky" || mode === "reveal") &&
                sectionTop <= Math.max(headerBottom, 0) + 1;
            section.toggleAttribute("data-sticky", sticky);
            // A hidden reveal header also hides the independently positioned trail.
            const offset = !sticky
                ? 0
                : headerOffset > 0
                  ? -section.offsetHeight - 10
                  : headerHeight;
            section.style.setProperty("--breadcrumbs-offset", `${offset}px`);
            if (
                mode === "sticky" ||
                (mode === "reveal" && headerOffset === 0)
            ) {
                occupiedHeight += section.offsetHeight;
            }
        }
        const value = `${occupiedHeight}px`;
        if (
            document.body.style.getPropertyValue("--sticky-content-height") !==
            value
        ) {
            document.body.style.setProperty("--sticky-content-height", value);
        }
    }
}
